import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@monksflow/monks-ui';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { BrandMediaItem } from '@/entities/brand-media';
import { usePermissions } from '@/entities/user';

import { allCarryTag, useApplyTag, useRemoveTag } from '../model/useApplyTag';

/**
 * Puts the tag this row stands for on whatever the grid has chosen, or takes it off again.
 *
 * The other way round from the header's control: there the tag is typed and the selection is what it
 * lands on, here the tag is already on screen and one click is the whole gesture. Which is what makes
 * a tag reusable -- naming it once and reaching for it afterwards, rather than retyping the same pair
 * onto every asset that needs it.
 *
 * One control that reads the selection rather than an add and a remove side by side. Every row in
 * the rail would otherwise carry two buttons, one of which is always the wrong one, and on a
 * selection that already carries the tag the add does nothing but say so. Which way it points is a
 * fact about the selection, so the selection is what decides it: all of them carrying the pair is
 * the only state where taking it off is the useful move.
 */
export const ApplyTagButton = ({
  tagKey,
  value,
  rows,
  stale = false,
}: {
  tagKey: string;
  value: string;
  rows: readonly BrandMediaItem[];
  /**
   * Whether the rows this would write from are known to be behind the server.
   *
   * The write sends each asset's whole list for the key, rebuilt from the row in hand, so a row
   * read before a library-wide rename or removal would put the old value back as a side effect of
   * adding an unrelated one. The host knows when such a write is in flight; this only has to
   * refuse to act on what it was given until the listing has caught up.
   */
  stale?: boolean;
}) => {
  const { t } = useTranslation('tools');
  const { apply, isPending: isApplying } = useApplyTag();
  const { remove, isPending: isRemoving } = useRemoveTag();
  const { canManageBrandAssets } = usePermissions();

  const removes = allCarryTag(rows, tagKey, value);
  const isPending = isApplying || isRemoving;

  const label = removes
    ? t('brandMedia.untagSelected', { value, count: rows.length })
    : t('brandMedia.applyTag', { value, count: rows.length });

  return (
    <Tooltip>
      {/* The span takes the pointer events a disabled button drops, so the reason still shows. */}
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <Button
            variant="ghost"
            size="icon"
            // The negative margin keeps a 24px target from setting the row's height: without it a row
            // carrying this button stands taller than the key row above it, in the same list.
            // Kept on focus as well as hover, or reaching it by keyboard hides it as it arrives.
            className="-my-0.5 size-6 shrink-0 opacity-0 transition-opacity duration-fast ease-out group-hover:opacity-100 focus-visible:opacity-100"
            disabled={isPending || stale || !canManageBrandAssets}
            aria-label={label}
            // The row underneath is a filter; putting its tag on something is not filtering by it.
            onClick={(event) => {
              event.stopPropagation();
              void (removes ? remove(rows, tagKey, value) : apply(rows, tagKey, value));
            }}
          >
            {removes ? <MinusIcon className="size-4" /> : <PlusIcon className="size-4" />}
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {!canManageBrandAssets
          ? t('brandMediaLibrary.permissions.noPermission')
          : stale
            ? t('brandMedia.tagsCatchingUp')
            : label}
      </TooltipContent>
    </Tooltip>
  );
};
